import {
  CriterionFragment,
  GroupPeerOutcomeFragment,
  ScaleItemFragment,
} from "@/generated/graphql";

export interface PeerOutcomeEntry {
  criteriaId: string;
  criteriaValue: string;
  criteriaDescription: string | null;
  scaleItems: {
    scaleId: string;
    scaleValue: string;
    scaleDescription: string;
  }[];
  students: {
    studentId: string;
    studentName: string;
    scales: {
      scaleId: string;
      marked: boolean;
    }[];
  }[];
}

export type PeerOutcomesMap = PeerOutcomeEntry[];

export const CRITERIA_OUTCOME_STUDENTS_LABEL = "Students";

export const getCriteriaTitle = (index: number, title: string | null) =>
  `Criteria ${index + 1}: ${title ?? ""}`;

// -- Type guards --

type ValidCriterion = CriterionFragment & { id: string; title: string };
type ValidScaleItem = ScaleItemFragment & {
  id: string;
  scaleLabel: string;
  description: string;
};

const isValidCriterion = (
  criterion: CriterionFragment
): criterion is ValidCriterion =>
  !!criterion.id && !!(criterion.skill?.skill ?? criterion.title);

const isValidScaleItem = (
  scaleItem: ScaleItemFragment
): scaleItem is ValidScaleItem =>
  !!scaleItem.id && !!scaleItem.scaleLabel && !!scaleItem.description;

// -- Utilities --

const createStudentNameMap = (
  members: { id: string; name: string }[]
): Map<string, string> => new Map(members.map((m) => [m.id, m.name]));

type CriterionMeta = Omit<PeerOutcomeEntry, "students">;

const createCriteriaMetaMap = (
  criteria: CriterionFragment[]
): Map<string, CriterionMeta> =>
  new Map(
    criteria.filter(isValidCriterion).map((criterion) => [
      criterion.id,
      {
        criteriaId: criterion.id,
        criteriaValue: criterion.skill?.skill ?? criterion.title,
        criteriaDescription:
          criterion.description != null && criterion.description !== ""
            ? criterion.description
            : null,
        scaleItems: criterion.scaleItems
          .filter(isValidScaleItem)
          .sort((a, b) => a.order - b.order)
          .map((item) => ({
            scaleId: item.id,
            scaleValue: item.scaleLabel,
            scaleDescription: item.description,
          })),
      },
    ])
  );

const initializeEmptyOutcomes = (
  criteriaMetaMap: Map<string, CriterionMeta>,
  members: { id: string; name: string }[]
): Map<string, PeerOutcomeEntry> => {
  const grouped = new Map<string, PeerOutcomeEntry>();

  for (const [criterionId, meta] of criteriaMetaMap.entries()) {
    grouped.set(criterionId, {
      ...meta,
      students: members.map((member) => ({
        studentId: member.id,
        studentName: member.name,
        scales: meta.scaleItems.map((scale) => ({
          scaleId: scale.scaleId,
          marked: false,
        })),
      })),
    });
  }

  return grouped;
};

// -- Main Transformer --

export const transformPeerOutcomes = (
  groupPeerOutcomes: GroupPeerOutcomeFragment[] | null,
  criteria: CriterionFragment[],
  members: { id: string; name: string }[],
  selfId: string,
  selfEvaluationEnabled: boolean
): PeerOutcomesMap => {
  const studentNameById = createStudentNameMap(members);
  const criteriaMetaMap = createCriteriaMetaMap(criteria);
  const groupedOutcomesByCriterion = initializeEmptyOutcomes(
    criteriaMetaMap,
    members
  );
  const givenCriterionOutcomes = groupPeerOutcomes
    ? groupPeerOutcomes.flatMap((outcome) => outcome.givenCriterionOutcomes)
    : [];

  if (givenCriterionOutcomes) {
    for (const outcome of givenCriterionOutcomes) {
      const targetStudentId = outcome.targetUserId;

      if (!selfEvaluationEnabled && targetStudentId === selfId) {
        continue;
      }

      const criterionId = outcome.markerRubricCriterionId;
      const selectedScaleId = outcome.markerRubricScaleItemId;
      const targetStudentName =
        studentNameById.get(targetStudentId ?? "") ?? "";

      const criterionEntry = groupedOutcomesByCriterion.get(criterionId);
      if (!criterionEntry) continue;

      let studentEntry = criterionEntry.students.find(
        (s) => s.studentId === targetStudentId
      );

      if (!studentEntry) {
        studentEntry = {
          studentId: targetStudentId ?? "",
          studentName: targetStudentName,
          scales: criterionEntry.scaleItems.map((scale) => ({
            scaleId: scale.scaleId,
            marked: false,
          })),
        };
        criterionEntry.students.push(studentEntry);
      }

      for (const scale of studentEntry.scales) {
        scale.marked = scale.scaleId === selectedScaleId;
      }
    }
  }

  return criteria
    .filter(isValidCriterion)
    .map((c) => groupedOutcomesByCriterion.get(c.id!))
    .filter((v): v is PeerOutcomeEntry => !!v);
};

export const getEntityId = (
  assessmentId: string,
  source?: string,
  target?: string
) => `${assessmentId}-peer-${source ?? ""}-${target ?? ""}`;
